// src/hooks/useRecentTasks.ts
// 從 IndexedDB 載入最近的任務（本地優先策略）

import { useState, useEffect } from 'react';
import { Task } from '../types/task';
import { logError } from '../utils/logger';

interface UseRecentTasksOptions {
  limit?: number;
  includeCompleted?: boolean;
}

/**
 * 從 IndexedDB 載入最近的任務
 *
 * @param userId 用戶 ID
 * @param limit 最多載入筆數（預設 100）
 * @param options 選項
 * @returns 任務列表
 */
export function useRecentTasks(
  userId: string,
  limit: number = 100,
  options?: UseRecentTasksOptions
): Task[] {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTasks = async () => {
      if (!userId) {
        setTasks([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // 動態載入 taskRepository（避免循環依賴）
        const { taskRepository } = await import('../services/indexedDB/repositories/taskRepository');

        // 從 IndexedDB 載入所有任務（已按 userId 索引）
        const allTasks = await taskRepository.getAll(userId, { limit: limit * 2 }); // 載入更多以支援過濾

        // 過濾並排序
        let filteredTasks = allTasks;

        // 若不包含已完成任務，過濾掉 CLOSED
        if (options?.includeCompleted === false) {
          filteredTasks = filteredTasks.filter(task => task.status === 'OPEN');
        }

        // 按更新時間排序（最近的在前）
        filteredTasks.sort((a, b) => {
          const aTime = a.updatedAt?.getTime() || a.createdAt?.getTime() || 0;
          const bTime = b.updatedAt?.getTime() || b.createdAt?.getTime() || 0;
          return bTime - aTime;
        });

        // 限制數量
        const limitedTasks = filteredTasks.slice(0, limit);

        setTasks(limitedTasks);
      } catch (error) {
        logError('Failed to load recent tasks from IndexedDB', error);
        setTasks([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTasks();
  }, [userId, limit, options?.includeCompleted]);

  return tasks;
}
